import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import StorageIcon from '@mui/icons-material/Storage';
import InsightsIcon from '@mui/icons-material/Insights';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import DescriptionIcon from '@mui/icons-material/Description';
import DashboardIcon from '@mui/icons-material/Dashboard';
import TuneIcon from '@mui/icons-material/Tune';
import GroupIcon from '@mui/icons-material/Group';
import PaymentIcon from '@mui/icons-material/Payment';
import { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from './Sidebar';

const TOPBAR_HEIGHT = 64;

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  children?: MenuItem[];
}

const sampleMenuItems: MenuItem[] = [
  {
    text: 'Home',
    icon: <HomeIcon />,
    path: '/samples',
  },
  {
    text: 'Data',
    icon: <StorageIcon />,
    path: '/samples/data',
  },
  {
    text: 'Insights',
    icon: <InsightsIcon />,
    path: '/samples/insights',
    children: [
      { text: 'Analytics', icon: <AnalyticsIcon />, path: '/samples/insights/analytics' },
      { text: 'Reports', icon: <DescriptionIcon />, path: '/samples/insights/reports' },
      { text: 'Dashboards', icon: <DashboardIcon />, path: '/samples/insights/dashboards' },
    ],
  },
  {
    text: 'Config',
    icon: <SettingsIcon />,
    path: '/samples/config',
    children: [
      { text: 'Settings', icon: <TuneIcon />, path: '/samples/config/settings' },
      { text: 'Team', icon: <GroupIcon />, path: '/samples/config/team' },
      { text: 'Billing', icon: <PaymentIcon />, path: '/samples/config/billing' },
    ],
  },
];

interface SampleSidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
}

const SampleSidebar: React.FC<SampleSidebarProps> = ({ collapsed, onToggleCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openSections, setOpenSections] = useState<string[]>(['Insights', 'Config']);

  const handleToggle = (text: string) => {
    if (collapsed) return;
    setOpenSections((prev) =>
      prev.includes(text) ? prev.filter((t) => t !== text) : [...prev, text]
    );
  };

  const isActive = (path: string) => {
    if (path === '/samples') {
      return location.pathname === '/samples';
    }
    return location.pathname.startsWith(path);
  };

  const currentWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  const renderMenuItem = (item: MenuItem, depth = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isOpen = openSections.includes(item.text) && !collapsed;
    const active = isActive(item.path);

    return (
      <React.Fragment key={item.path}>
        <ListItem disablePadding>
          <Tooltip title={collapsed ? item.text : ''} placement="right" arrow>
            <ListItemButton
              onClick={() => {
                if (hasChildren && !collapsed) {
                  handleToggle(item.text);
                  navigate(item.path); // Navigate to parent page AND toggle submenu
                } else if (hasChildren && collapsed) {
                  navigate(item.path);
                } else {
                  navigate(item.path);
                }
              }}
              sx={{
                pl: collapsed ? 2 : 2 + depth * 2,
                py: 1.25,
                mx: 1,
                borderRadius: 1,
                justifyContent: collapsed ? 'center' : 'flex-start',
                bgcolor: active ? 'rgba(244, 162, 97, 0.2)' : 'transparent',
                '&:hover': {
                  bgcolor: active ? 'rgba(244, 162, 97, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: 'text.onDark',
                  minWidth: collapsed ? 0 : 40,
                  mr: collapsed ? 0 : 'auto',
                  opacity: active && !hasChildren ? 1 : 0.8,
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!collapsed && (
                <>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '0.9rem',
                      fontWeight: active && !hasChildren ? 600 : 400,
                    }}
                    sx={{ color: 'text.onDark' }}
                  />
                  {hasChildren && (
                    <Box sx={{ color: 'text.onDark', opacity: 0.7 }}>
                      {isOpen ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                  )}
                </>
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>

        {hasChildren && !collapsed && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children!.map((child) => renderMenuItem(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: currentWidth,
        flexShrink: 0,
        transition: 'width 0.2s ease-in-out',
        '& .MuiDrawer-paper': {
          width: currentWidth,
          boxSizing: 'border-box',
          bgcolor: 'background.sidebar',
          borderRight: 'none',
          mt: `${TOPBAR_HEIGHT}px`,
          height: `calc(100% - ${TOPBAR_HEIGHT}px)`,
          transition: 'width 0.2s ease-in-out',
          overflowX: 'hidden',
        },
      }}
    >
      {/* Collapse/Expand Toggle */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: collapsed ? 'center' : 'flex-end',
          p: 1,
          borderBottom: 1,
          borderColor: 'rgba(255, 255, 255, 0.1)',
        }}
      >
        <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
          <IconButton
            onClick={onToggleCollapse}
            size="small"
            sx={{
              color: 'text.onDark',
              opacity: 0.7,
              '&:hover': {
                opacity: 1,
                bgcolor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      <List sx={{ pt: 1 }}>
        {sampleMenuItems.map((item) => renderMenuItem(item))}
      </List>
    </Drawer>
  );
};

const SampleLayout: React.FC = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // v2.55 - Use Clerk login page - 2026-02-04
  const handleLogin = () => {
    navigate('/login');
  };

  const handleGetStarted = () => {
    navigate('/register'); // Go to consent page first
  };

  const currentSidebarWidth = sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Top Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ height: TOPBAR_HEIGHT, minHeight: TOPBAR_HEIGHT }}>
          {/* Logo / Home */}
          <Tooltip title="Back to Home">
            <IconButton
              onClick={() => navigate('/')}
              sx={{ mr: 1, color: 'secondary.main' }}
            >
              <HomeIcon />
            </IconButton>
          </Tooltip>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'secondary.main',
              cursor: 'pointer',
            }}
            onClick={() => navigate('/')}
          >
            mylojic.com
          </Typography>

          {/* Spacer */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Sample Badge */}
          <Box
            sx={{
              bgcolor: 'warning.light',
              color: 'warning.contrastText',
              px: 2,
              py: 0.5,
              borderRadius: 1,
              mr: 3,
              fontWeight: 600,
              fontSize: '0.85rem',
            }}
          >
            DEMO MODE
          </Box>

          {/* Auth Buttons */}
          <Button
            onClick={handleLogin}
            sx={{
              color: 'text.primary',
              fontWeight: 500,
              mr: 2,
              '&:hover': { color: 'primary.main' },
            }}
          >
            Login
          </Button>
          <Button
            variant="contained"
            onClick={handleGetStarted}
            sx={{
              bgcolor: 'accent.main',
              color: 'accent.contrastText',
              fontWeight: 600,
              px: 3,
              '&:hover': { bgcolor: 'accent.dark' },
            }}
          >
            Get Started
          </Button>
        </Toolbar>
      </AppBar>

      {/* Sidebar - reuse the same component but for sample routes */}
      <SampleSidebar
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      {/* Main Content with Watermark */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: `${currentSidebarWidth}px`,
          mt: `${TOPBAR_HEIGHT}px`,
          bgcolor: 'background.default',
          minHeight: `calc(100vh - ${TOPBAR_HEIGHT}px)`,
          transition: 'margin-left 0.2s ease-in-out',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Content */}
        <Box sx={{ p: 3, position: 'relative', zIndex: 1 }}>
          <Outlet />
        </Box>

        {/* Diagonal SAMPLE Watermark */}
        <Box
          sx={{
            position: 'fixed',
            top: TOPBAR_HEIGHT,
            left: currentSidebarWidth,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            overflow: 'hidden',
            zIndex: 0,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%) rotate(-35deg)',
              whiteSpace: 'nowrap',
            }}
          >
            <Typography
              sx={{
                fontSize: '8rem',
                fontWeight: 900,
                color: 'rgba(0, 51, 102, 0.06)',
                letterSpacing: '0.5rem',
                userSelect: 'none',
              }}
            >
              SAMPLE
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SampleLayout;
